import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
} from "@mui/material";
import {
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortItem,
} from "@mui/x-data-grid";
import { useState } from "react";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import CustomSelect from "../../../../components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import Flex from "../../../../components/Flex";
import GlobalFilter from "../../../../components/GlobalFilter";
import { GlobalTableHeightWrapper } from "../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../components/RenderWithTooltip";
import SearchInput from "../../../../components/SearchInput";
import StatusChip from "../../../../components/StatusChip";
import Text from "../../../../components/Text";
// import WithInfo from "../../../../components/WithInfo"; // unused after batch resolve removed
import ActionMenu from "../../../../components/app/ActionMenu";
import ConfirmationModal from "../../../../components/app/ConfirmationModal";
import DeleteModal from "../../../../components/app/DeleteModal";
// import StyledButtonV2 from "../../../../components/app/StyledButtonV2"; // unused after batch resolve removed
import PatientContentWrap from "../../../../components/app/provider/patient/add-new-patient/PatientContentWrap";
import { useActiveComponentCountController } from "../../../../components/filter/use-ActiveComponentCountController";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { PRIORITY_MAPPING } from "../../../../pages/app/provider/collabration/collaboration-referral-constant";
import {
  DEFAULT_FILTERS,
  FILTER_KEYS,
  FLAG_MAPPING,
  INITIAL_EXPANDED,
  TaskFilters,
  dynamicColumns,
} from "../../../../pages/app/provider/collabration/collaboration-task-constant";
import UpsertTaskDrawer from "../../../../pages/app/provider/collabration/tasks/UpsertTaskDrawer";
import SendBulkEmailModal from "../../../../pages/app/provider/communications/email/SendBulkEmailModal";
import { useListTasks } from "../../../../queries/collaboration/tasks/use-list-task";
import { useArchiveTask } from "../../../../queries/collaboration/tasks/use-task-archive";
import { useTaskCancel } from "../../../../queries/collaboration/tasks/use-task-cancel";
import { useProvidersList } from "../../../../queries/provider/setting/use-providers-list";
import {
  GetAllTasksTaskPriorityEnum,
  GetAllTasksTaskStatusEnum,
} from "../../../../services";
import {
  formatDate,
  formatDateTime,
  formatTime,
} from "../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../utils/utils";
import ResolvePatientTask from "./ResolvePatientTask";
import { getStoredRealm } from "@/utils/auth-storage";

const PatientPortalTask = () => {
  const [pagination, setPagination] =
    useState<GridPaginationModel>(DEF_PAGINATION);
  // const { hasUpdate, hasDelete, hasReadOnly } = useRoleController("Task");

  // Row selection removed - batch resolve not supported in patient portal
  // const [rowSelectionModel, setRowSelectionModel] =
  //   useState<GridRowSelectionModel>([]);
  const [taskFilter, setTaskFilter] = useState<TaskFilters>(DEFAULT_FILTERS);
  const [searchStringKey, setSearchStringKey, instantSearchVal] =
    useDebounce<string>("");
  const [expanded, setExpanded] =
    useState<Record<string, boolean>>(INITIAL_EXPANDED);
  const [todoTaskFilter, setTodoTaskFilter] = useState({ toDoTask: true });

  const [selectedTaskModel, setSelectedTaskModel] =
    useState<GridRowSelectionModel>([]);
  const [
    taskDisclosureHost,
    resolveTaskDisclosureHost,
    deleteTaskDisclosureHost,
    cancelTaskDisclosureHost,
    sendMailDisclosure,
    confirmResolveDisc,
  ] = useReduceDisclosure(6);
  const { mutateAsync: archiveTask } = useArchiveTask();
  const { mutateAsync: cancelTask } = useTaskCancel();

  const dueDate = (() => {
    if (!taskFilter.due_date) return undefined;
    const date = new Date(taskFilter.due_date);
    if (isNaN(date.getTime())) return undefined;
    date.setHours(23, 59, 59, 999);
    return date;
  })();

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [taskType, _setTaskType] = useState<string>("");
  const [patient] = useCurrentUser();
  const {
    data: tasks,
    isFetching,
    refetch,
  } = useListTasks({
    pagination,
    gridSorting,
    taskStatus: taskFilter.status as GetAllTasksTaskStatusEnum,
    taskPriority: taskFilter.priority as GetAllTasksTaskPriorityEnum,
    patient: patient?.uuid,
    providerTask: false,
    dueDate: dueDate ? dueDate.toISOString() : undefined,
    assignTo: getIdFromObj(taskFilter.assign_to),
    assignBy: getIdFromObj(taskFilter.assign_by),
    archive: false,
    xTENANTID: getXtenantId(),
    myTask: true,
    toDoTask: todoTaskFilter.toDoTask,
    searchString: searchStringKey?.length ? searchStringKey : undefined,
  });

  const COLUMNS = applyDefaultColumnSettings(
    [
      {
        field: "taskTitle",
        headerName: "Title",
        flex: 0.1,
        renderCell: ({ row }) => <RenderWithTooltip value={row?.title} />,
      },
      {
        field: "due_date",
        headerName: "Due Date & Time",
        flex: 0.14,
        renderCell: ({ row }: { row: any }) => (
          <Flex gap={0.3}>
            <Text>{formatDate(row.dueDate)}</Text>,
            <Text>{formatTime(row.dueDate)}</Text>
          </Flex>
        ),
      },
      {
        field: "taskStatus",
        headerName: "Status",
        flex: 0.1,
        renderCell: ({ row }: { row: any }) => {
          const status = row?.taskStatus as string | undefined;
          const label = status
            ? replaceUnderScoreAddSpace(status?.toLowerCase())
            : "-";
          const variantMap: Record<
            string,
            "success" | "error" | "warning" | "pending"
          > = {
            RESOLVED: "success",
            OVERDUE: "error",
            PENDING: "pending",
            TO_DO: "warning",
          };
          return (
            <StatusChip
              label={label}
              variant={status ? (variantMap[status] ?? "default") : "default"}
            />
          );
        },
      },
      {
        field: "task_priority",
        headerName: "Priority",
        flex: 0.1,
        renderCell: ({ row }: { row: any }) => {
          const priorityConfig =
            PRIORITY_MAPPING[row?.priority as keyof typeof PRIORITY_MAPPING];
          return (
            <Chip
              size="small"
              label={priorityConfig?.label ?? "-"}
              color="default"
              sx={{
                color: priorityConfig?.color || "black",
                bgcolor: "white",
              }}
            />
          );
        },
      },
      {
        field: "assignBy",
        headerName: "Assigned By",
        flex: 0.12,
        renderCell: ({ row }) => (
          <RenderWithTooltip value={getValueFromObj(row?.assignBy) as any} />
        ),
      },
      {
        field: "assignTo",
        headerName: "Task Reviewer",
        flex: 0.12,
        renderCell: ({ value }: any) => (
          <RenderWithTooltip value={getValueFromObj(value) as any} />
        ),
      },
      {
        field: "resolvedDate",
        headerName: "Resolved on",
        flex: 0.12,
        renderCell: ({ row }) => (
          <Flex gap={0.6} flexDirection={"column"}>
            <RenderWithTooltip
              value={row.resolvedDate ? formatDateTime(row.resolvedDate) : "-"}
            />
          </Flex>
        ),
      },
      {
        field: "resolvedBy",
        headerName: "Resolved By",
        flex: 0.12,
        renderCell: ({ row }) => <RenderWithTooltip value={row?.resolvedBy} />,
      },
      {
        field: "description",
        headerName: "Note",
        flex: 0.15,
        renderCell: ({ row }: { row: any }) => (
          <RenderWithTooltip value={row?.description} />
        ),
      },
      {
        field: "flagStatus",
        headerName: "Flags",
        flex: 0.08,
        renderCell: ({ row }: { row: any }) => {
          const flagConfig =
            FLAG_MAPPING[row?.flagStatus as keyof typeof FLAG_MAPPING] ||
            FLAG_MAPPING.NONE;
          const Icon = flagConfig?.icon;
          return Icon ? (
            <Icon fontSize="small" sx={{ color: flagConfig.color }} />
          ) : (
            "-"
          );
        },
      },
      {
        field: "Action",
        headerName: "Actions",
        renderCell: ({ row }: any) => {
          if (row?.taskStatus === "RESOLVED") return null;
          return (
            <ActionMenu
              options={[
                {
                  value: "resolve",
                  label: "Resolve",
                },
              ]}
              onActionClick={(action) => {
                if (action === "resolve")
                  resolveTaskDisclosureHost.open([row?.uuid]);
              }}
            />
          );
        },
      },
    ],
    ["due_date", "task_priority", "taskTitle"]
  );

  async function archiveHandler<T>(value: boolean, id: T) {
    return archiveTask({
      taskId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  async function cancelTaskHandler<T>(id: T) {
    return cancelTask({
      bulkCancel: { taskId: [id as string] },
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  // const [me] = useCurrentUser();

  // const getCurrentUser = (assignTo) =>
  //     me?.uuid === getIdFromObj(assignTo);

  const columns = dynamicColumns(COLUMNS, true, false, todoTaskFilter) ?? [];

  // Filter bar state
  const [localFilters, setLocalFilters] = useState<TaskFilters>(taskFilter);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const activeFilterCount = useActiveComponentCountController(taskFilter);
  const [searchProviderKey, setSearchProviderKey] = useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: providersListFetching } =
    useProvidersList({
      pagination: providerPagination,
      searchKey: searchProviderKey,
      xTENANTID: getXtenantId(),
      enabled: expanded["assign_to"],
    });

  const handleExpandClick = (key: string, newExpanded: boolean) => {
    setExpanded({ ...INITIAL_EXPANDED, [key]: newExpanded });
  };

  const handleApply = () => {
    setTaskFilter(localFilters);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setAnchorEl(null);
  };

  return (
    <PatientContentWrap
      title={"Task"}
      hideBackButton
      endAction={
        <Flex
          justifyContent={"end"}
          height={"2.5rem"}
          gap={1}
          alignItems={"center"}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={todoTaskFilter.toDoTask}
                onChange={(e) =>
                  setTodoTaskFilter({ toDoTask: e.target.checked })
                }
              />
            }
            label="To-Do Tasks"
          />

          {/* Batch Resolve removed - patient portal supports single resolve only
          {todoTaskFilter.toDoTask && (
            <WithInfo title="Batch Resolve will available soon">
              <StyledButtonV2
                // disabled={selectedTaskModel?.length === 0}
                disabled={true}
                label="Batch Resolve"
                variant="contained"
                onClick={() =>
                  resolveTaskDisclosureHost.open(selectedTaskModel)
                }
              />
            </WithInfo>
          )} */}

          {/* <WithInfo title="Batch Reassign will available soon">
                <StyledButtonV2
                    // disabled={selectedTaskModel?.length === 0}
                    disabled={true}
                    label="Batch Reassign"
                    variant="contained"
                    onClick={() => reassignTaskDisclosureHost.open(selectedTaskModel)}
                />
            </WithInfo> */}

          <Box width={"18rem"}>
            <SearchInput
              searchKey={instantSearchVal ?? ""}
              onSearch={(v) => setSearchStringKey(v)}
              onClear={() => setSearchStringKey("")}
              placeholder="Task Title"
              name="searchString"
            />
          </Box>

          <GlobalFilter
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            activeFilterCount={activeFilterCount}
          >
            <Flex width={"100%"} justifyContent={"end"}>
              <Button
                variant="text"
                onClick={() => {
                  setLocalFilters(DEFAULT_FILTERS);
                  setTaskFilter(DEFAULT_FILTERS);
                }}
              >
                Clear Filter
              </Button>
            </Flex>
            {FILTER_KEYS.map((it) =>
              it === "due_date" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <CustomDatePicker
                      value={localFilters[it as keyof TaskFilters] ?? null}
                      onChange={(val) =>
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof TaskFilters]: val,
                        })
                      }
                    />
                  </AccordionDetails>
                </Accordion>
              ) : it === "assign_to" || it === "assign_by" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <CustomSelectScroll
                      placeholder={replaceUnderScoreAddSpace(it)}
                      loading={providersListFetching}
                      options={providersList?.data?.content?.map((p) => ({
                        value: p?.uuid,
                        label: p?.firstName + " " + p?.lastName,
                      }))}
                      onSearch={(_, val) => {
                        setSearchProviderKey(val);
                        setProviderPagination((p) => ({ ...p, page: 0 }));
                      }}
                      pagination={providerPagination}
                      setPagination={setProviderPagination}
                      totalElements={providersList?.data?.page?.totalElements}
                      value={
                        Object.keys(
                          localFilters[it as keyof TaskFilters] ?? {}
                        )?.[0] ?? undefined
                      }
                      onChange={(value, option) => {
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof TaskFilters]:
                            value && option
                              ? { [value]: option.label }
                              : undefined,
                        });
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              ) : it === "priority" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <CustomSelect
                      placeholder="Select Task Priority"
                      options={[
                        { value: "HIGH", label: "High" },
                        { value: "MEDIUM", label: "Medium" },
                        { value: "LOW", label: "Low" },
                      ]}
                      value={localFilters[it as keyof TaskFilters] ?? undefined}
                      onChange={(value) =>
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof TaskFilters]: value,
                        })
                      }
                    />
                  </AccordionDetails>
                </Accordion>
              ) : null
            )}
            <Box minWidth={220}>
              <Flex width={"100%"} justifyContent={"end"}>
                <Button
                  variant="text"
                  sx={{ color: "gray" }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button variant="text" onClick={handleApply}>
                  Apply
                </Button>
              </Flex>
            </Box>
          </GlobalFilter>
        </Flex>
      }
    >
      <Box width={"100%"} height={"100%"}>
        {/* Top Bar */}

        {/* Modals & Drawers */}
        <ConfirmationModal
          maxWidth={"sm"}
          onConfirm={() => resolveTaskDisclosureHost.open(selectedTaskModel)}
          disclosureHost={confirmResolveDisc}
          confirmationText="Yes"
          cancelText="No"
          title={
            <Text fontWeight={"medium"} fontSize={"24px"}>
              Do you want resolve this task?
            </Text>
          }
        />

        {taskDisclosureHost.opened && (
          <UpsertTaskDrawer
            patientData={{}}
            isProviderCentric={false}
            disclosureHost={taskDisclosureHost}
            taskType={taskType}
          />
        )}

        <ResolvePatientTask
          disclosureHost={resolveTaskDisclosureHost}
          setSelectedTaskModel={setSelectedTaskModel}
          // onResolve={ }
        />

        {/* {reassignTaskDisclosureHost.opened && (
                    <BatchReassignTaskDrawer
                        title="Reassign Task"
                        disclosureHost={reassignTaskDisclosureHost}
                        setSelectedTaskModel={setSelectedTaskModel}
                    />
                )} */}

        <DeleteModal
          w="380px"
          open={deleteTaskDisclosureHost.opened}
          onClose={deleteTaskDisclosureHost.close}
          id={deleteTaskDisclosureHost.data}
          onConfirmation={(id) => archiveHandler(true, id)}
          content={"You want to delete Task"}
        />
        <DeleteModal
          w="380px"
          open={cancelTaskDisclosureHost.opened}
          onClose={cancelTaskDisclosureHost.close}
          id={cancelTaskDisclosureHost.data}
          onConfirmation={(id) => cancelTaskHandler(id)}
          content={"You want to cancel Task"}
        />

        {sendMailDisclosure.opened && (
          <SendBulkEmailModal
            disClosureHost={sendMailDisclosure}
            onSave={() => {
              confirmResolveDisc.open();
              refetch();
            }}
          />
        )}

        {/* Data Grid */}
        <GlobalTableHeightWrapper>
          <CustomDataGridV2
            sortModel={gridSorting}
            onSortModelChange={setGridSorting}
            loading={isFetching}
            rows={tasks?.data?.content || []}
            rowCount={tasks?.data?.page.totalElements}
            getRowId={(row) => row.uuid}
            columns={columns}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            // Checkbox selection removed - batch resolve not supported in patient portal
            // checkboxSelection
            // keepNonExistentRowsSelected
            // onRowSelectionModelChange={(rowIdx) => {
            //   setRowSelectionModel(rowIdx);
            //   setSelectedTaskModel(rowIdx as string[]);
            // }}
            // rowSelectionModel={rowSelectionModel}
          />
        </GlobalTableHeightWrapper>
      </Box>
    </PatientContentWrap>
  );
};

export default PatientPortalTask;
